import * as React from 'react';
import {forwardRef, useEffect, useImperativeHandle, useRef} from 'react';
import {
    Button,
    IconButton,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableFooter,
    TableHead,
    TableRow, Typography,
} from '@mui/material';
import Grid from '@mui/material/Grid';
import DateTimePicker from '@mui/lab/DateTimePicker';
import config from '../../../app/config';
import TextField from '@mui/material/TextField';
import moment from 'moment';
import {useTranslation} from 'react-i18next';
import Paper from '@mui/material/Paper';
import NavigateBeforeSharpIcon from '@mui/icons-material/NavigateBeforeSharp';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import {ReportsAPI} from './reports_api';
import API from '../../../app/api';
import useDict from '../../../hooks/useDict';
import {setSnackbar} from '../../../store/reducers/globals';
import {setLoading} from '../../../store/reducers/patient';
import {useDispatch} from 'react-redux';
import Chip from '@mui/material/Chip';
import {DatabaseIconHL7, DateIconHL7} from '../../../components/icons/icons';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import ArrowDropUpIcon from '@mui/icons-material/ArrowDropUp';
import _ from 'lodash';
import {RowMessage, saveDataToCSV, useTableStyles} from './table_utils';
import {useWebSocket} from '../../../context/WebSocketProvider';


const columnsWidths = {
    'Info': '100px',
    'Date': '300px',
    'Created': '300px',
    'Type': '170px',
    'Hospital number': '170px',
    'Attendance': '200px',
    'System': '200px',
    'Version': '200px',
    'Actions': '200px',
    'Patient': '200px',
    'Status': '170px',
    'Last Send Time': '200px',
    'Last Status': '170px',
    'Hl7 Type': '170px',
};

const AuditsTable= forwardRef((props, ref) => {

    const isEyonix = process.env.BUILD_MODULE === "main";
    const {t} = useTranslation();
    const dispatch = useDispatch();
    const controller = new AbortController();
    const classes = useTableStyles();

    const {manageSubscription, message} = useWebSocket();

    const [runSocket, setRunSocket] = React.useState(props.liveMessage);

    const [messageType] = useDict('HL7MessageTypes');

    const [messages, setMessages] = React.useState([]);
    const [page, setPage] = React.useState(0);
    const [allPages, setAllPages] = React.useState(0);
    const [sortSend, setSortSend] = React.useState(null);
    const [sortOrder, setSortOrder] = React.useState(false);
    const [initStage, setInitStage] = React.useState(true);

    const [selectedRows, setSelectedRows] = React.useState([]);

    useEffect(() => {
        manageSubscription([props.webSocketChanel], runSocket);
    }, [runSocket]);

    const pageSize = 20;
    useImperativeHandle(ref, () => ({
        exportCsv: () => {
            dispatch(setLoading(true));
            let csv = props.columns.map((column) => {
                return column.name;
            }).join(',');
            getSetFilters();

            ReportsAPI.auditReport(getFilter(0, 100000)).then(tableData => {
                if (tableData?.data?.data) {
                    csv = saveDataToCSV(csv,tableData.data.data,props.columns);
                }
            }).finally(() => {
                const a = document.createElement("a");
                a.href = URL.createObjectURL(
                    new Blob([csv], { type: 'text/csv' })
                );
                a.setAttribute("download", "userPatientAudits.csv");
                document.body.appendChild(a);
                a.click();
                document.body.removeChild(a);
                dispatch(setLoading(false));
            });
        },
    }));
    useEffect(() => {
        if (props.columns.length) {
            let defaultSortItem = _.find(props.columns, {defaultSort: true});
            if (defaultSortItem) {
                setSortSend(defaultSortItem.value);
            } else {
                setSortSend('_createdDate');
            }
        }

    }, [props.columns]);

    const [searchCriteria, setSearchCriteria] = React.useState({
        searchFromDay: moment().subtract(1, 'month'),
        searchToDay: moment(),
        patientId: null,
        userName: null
    });

    useEffect(() => {
        if (props.liveMessage) {
            setRunSocket(props.liveMessage);
            getAuditReportData();
        } else {
            setRunSocket(props.liveMessage);
        }

    }, [props.liveMessage]);

    useEffect(() => {
        getAuditReportData();
    }, [message]);

    const getFilter = (_page,_pageSize) => {
        let filter = {
            from_date : moment.utc(searchCriteria.searchFromDay).format("YYYY-MM-DD HH:mm"),
            to_date :  moment.utc(searchCriteria.searchToDay).format("YYYY-MM-DD HH:mm"),
            patientId: searchCriteria.catalog,
            userName: searchCriteria.userName,
            order: `${(sortOrder ? '' : '!')}${sortSend}`,
            page: _page,
            pageSize: _pageSize
        };
        return filter;
    };

    const getAuditReportData = () => {
        if (sortSend) {
            dispatch(setLoading(true));
            getSetFilters();
            ReportsAPI.auditReport(getFilter(page, pageSize), controller.signal).then(tableData => {
                if (tableData?.data?.data) {
                    setAllPages(Math.ceil(tableData.data.allsize/pageSize));
                    setMessages(tableData.data.data);
                }
            }).finally(() => {
                dispatch(setLoading(false));
            }).catch(error => {
                dispatch(setSnackbar({
                    type: 'error',
                    message: t('Unknown error while fetching report data'),
                }));
            });
        }
    };

    useEffect(() => {
        //getHl7Message();

        return () => {
            controller.abort();
        };
    }, []);

    const [filtersChip, setFilterChips] = React.useState([]);

    const applyFilter = () => {
        setInitStage(false);
        if (!page) {
            getAuditReportData();
        } else
            setPage(0);
    };

    const getSetFilters = () => {
        let filters = [];

        if (searchCriteria.catalog) filters.push({filed: 'catalog', value: searchCriteria.catalog});
        if (searchCriteria.userName) filters.push({
            filed: 'userName',
            value: searchCriteria.userName,
        });
        setFilterChips(filters);
    };

    useEffect(() => {
        if (doSearch.current) {
            applyFilter();
            doSearch.current = false;
        }
    }, [searchCriteria]);

    const doSearch = useRef(false);

    const handleDelete = (chip) => {
        doSearch.current = true;
        setSearchCriteria(prevState => ({...prevState, [chip.filed]: ''}));
    };

    const renderFilters = () => {
        return (<Grid
            container
            spacing={2}
            justifyContent="center"
            alignItems="center"
            sx={{padding: '40px', borderRadius: '5px', marginBottom: '10px', border: '1px solid #448FFF'}}
        >
            <Grid item xs={3}>
                <DateTimePicker
                    label={t('Date From')}
                    inputFormat={config.defaultDateTimeFormat}
                    inputVariant="outlined"
                    clearable={true}
                    ampm={false}
                    value={searchCriteria.searchFromDay}
                    onChange={(value) => {
                        setSearchCriteria(prevState => ({...prevState, searchFromDay: value}));
                    }}
                    renderInput={(inp) => <TextField style={{width: '100%', margin: 0}} {...inp}
                                                     size={props.size}/>}
                />
            </Grid>
            <Grid item xs={3}>
                <DateTimePicker
                    label={t('Date To')}
                    inputFormat={config.defaultDateTimeFormat}
                    inputVariant="outlined"
                    clearable={true}
                    ampm={false}
                    value={searchCriteria.searchToDay}
                    onChange={(value) => {
                        setSearchCriteria(prevState => ({...prevState, searchToDay: value}));
                    }}
                    renderInput={(inp) => <TextField style={{width: '100%', margin: 0}} {...inp}
                                                     size={props.size}/>}
                />
            </Grid>
            <Grid item xs={3}>
                <TextField
                    name={'value'}
                    fullWidth
                    label={t('MRN/Hospital Number')}
                    fullEvent={true}
                    onChange={(event) => {
                        setSearchCriteria(prevState => ({...prevState, catalog: event.target.value}));
                    }}
                    value={searchCriteria.catalog}
                    variant="outlined"
                />
            </Grid>
            <Grid item xs={3}>
                <TextField
                    name={'value'}
                    fullWidth
                    label={t('User Name')}
                    fullEvent={true}
                    onChange={(event) => {
                        setSearchCriteria(prevState => ({...prevState, userName: event.target.value}));
                    }}
                    value={searchCriteria.userName}
                    variant="outlined"
                />
            </Grid>
            <Grid item xs={12}>
                <Button variant="contained" className={classes.agyleButton} onClick={applyFilter}>Apply</Button>
            </Grid>
            <Grid item xs={12}>
                {filtersChip.map((filter) => {
                    return <Chip label={filter.value} onDelete={() => handleDelete(filter)}/>;
                })}
            </Grid>
        </Grid>);
    };


    const addRow = (row) => {
        setSelectedRows((prRows) => ([...prRows, row]));
    };

    const removeRow = (row) => {
        let selectedRowsTmp = selectedRows.filter(item => item !== row);
        setSelectedRows(selectedRowsTmp);
    };

    useEffect(() => {
        if (sortSend && !initStage) {
            getAuditReportData();
            //console.log("sortSend,sortOrder,page",sortSend, sortOrder, page)
        }
    }, [sortSend, sortOrder, page]);


    const handleChangeNextPage = () => {
        setPage(page + 1);
    };

    const handleChangePrevPage = () => {
        setPage(page - 1);
    };

    const resendMessages = () => {
        let promises = [];

        selectedRows.forEach((row) => {
            promises.push(API.sendMessageHL7(row.message, controller.signal));
        });

        Promise.all(promises).then((values) => {
            values.forEach((val) => {
                if (val.status === 'ok')
                    dispatch(setSnackbar({type: 'info', message: ` The message has been resent!`}));
                else
                    dispatch(setSnackbar({type: 'error', message: ` The message was not sent!`}));
            });
        });

    };

    const getColumnWidth = column => (columnsWidths[column?.description] || '256px');


    const handleSetSort = (column) => {
        if (column.value === sortSend) {
            setSortOrder(!sortOrder);
        } else {
            setSortSend(column.value);
        }
    };

    const getSortIcon = (columnName) => {
        if (columnName !== sortSend) return '';
        if (sortOrder) return <ArrowDropDownIcon/>;
        return <ArrowDropUpIcon/>;

    };
    return (<div>
        {!props.filterHidden && renderFilters()}
        {!props.outcoming && !props.filterHidden && selectedRows.length > 0 && <React.Fragment>
            <Button className={classes.agyleButton}
                    onClick={() => {}}
                    size="small"
                    color="primary"
                    variant="contained">
                {t('copy')}
            </Button>
            <Button className={classes.agyleButton}
                    onClick={resendMessages}
                    size="small"
                    color="primary"
                    variant="contained">
                {t('resend')}
            </Button>
        </React.Fragment>}

        <Paper sx={{width: '100%', overflow: 'hidden', mt: 4}}>
            <TableContainer>
                <Table stickyHeader aria-label="sticky table">
                    <TableHead key={'HEAD'}>
                        <TableRow>
                            {props.columns.map((column, indexColumn) => {
                                return (
                                    <TableCell
                                        key={indexColumn + 'Hasd'}
                                        align={column.align}
                                        style={{
                                            minWidth: column.minWidth,
                                            width: column.maxWidth || getColumnWidth(column),
                                            border: '1px solid #80808035',
                                        }}
                                    >
                                        <Grid container justifyContent="space-between">
                                            <Typography variant="subtitle2"
                                                        color="textSecondary"
                                                        style={{cursor: column.sort ? 'pointer' : ''}}
                                                        onClick={() => {
                                                            if (column.sort) {
                                                                handleSetSort(column);
                                                            }
                                                        }}>
                                                <div style={{display: 'flex'}}>
                                                    {column.description} {getSortIcon(column.value)}
                                                </div>

                                            </Typography>
                                            {indexColumn === props.columns.length - 1 &&
                                                <DatabaseIconHL7 onClick={props.openColumnManager} style={{cursor: "pointer"}}/>}
                                            {column.name === 'dateTime' && <DateIconHL7/>}
                                        </Grid>
                                    </TableCell>
                                );
                            })}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {messages?.map((row, rowIndex) => {
                            // console.log("test", row);
                            return <React.Fragment key={rowIndex + 'div'}>
                                <RowMessage columns={props.columns}
                                            messagePath={props.messagePath}
                                            row={row} handleAddRow={addRow}
                                            rowIndex={rowIndex}
                                            outcoming={props.outcoming || false}
                                            handleRemoveRow={removeRow}/>
                            </React.Fragment>;
                        })}
                    </TableBody>
                    <TableFooter>
                        <TableRow>
                            <TableCell colSpan={8}>
                                <IconButton size="small"
                                            disabled={page < 1}
                                            onClick={handleChangePrevPage}>
                                    <NavigateBeforeSharpIcon fontSize="small"/>
                                </IconButton>
                                Page: {page + 1} from {allPages}
                                <IconButton size="small"
                                            disabled={(page+1) >= allPages}
                                            onClick={handleChangeNextPage}>
                                    <NavigateNextIcon fontSize="small"/>
                                </IconButton>
                            </TableCell>
                        </TableRow>
                    </TableFooter>
                </Table>
            </TableContainer>
        </Paper>

    </div>);
})

export default AuditsTable;